import { Button, cn } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  PaperPlaneTilt as PaperPlaneIcon,
  Seal as SealIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useState } from "react";
import { toaster } from "@/app";
import { confirmDialog } from "@/components/confirm-dialog";
import { apiClient } from "@/lib/api/client";
import type { paths } from "@/lib/api/schema";
import {
  DiffusionDialog,
  type DiffusionTarget,
} from "../document-table/diffusion-dialog";
import {
  NOTICE_FLAG_CLASSES,
  NOTICE_FLAG_LABELS,
  NoticeFlagSelect,
} from "../document-table/notice-flag-select";

type ObservationItem =
  paths["/api/yards/{yardId}/observations"]["get"]["responses"][200]["content"]["application/json"]["data"]["items"][number];
export type Consigne = ObservationItem["consignes"][number];
type ConsigneKind = Consigne["kind"];
type CandidateDocument =
  paths["/api/yards/{yardId}/observations/{observationId}/consigne-documents"]["get"]["responses"][200]["content"]["application/json"]["data"]["items"][number];

const KINDS: { kind: ConsigneKind; title: string }[] = [
  { kind: "auteur", title: "Consignes à auteur des documents visés" },
  { kind: "autre_prestataire", title: "Consignes à autre prestataire" },
];

// Ready-made author consignes from the screen 022 mockup; any text is allowed.
const AUTEUR_SUGGESTIONS = [
  "À prendre en compte en l'état",
  "Proposer réponse",
];

const SCOPE_GROUPS: Record<CandidateDocument["scope"], string> = {
  vise: "Documents visés",
  meme_auteur: "Autres documents du même auteur (dernière version)",
  autre_auteur: "",
};

type Draft = {
  consigneId: number | null;
  kind: ConsigneKind;
  description: string;
  documentVersionIds: number[];
  /** Attachments recorded in ÉtudesCarré. */
  pieceJointeVersionIds: number[];
  /** Uploaded attachments kept. */
  fichierIds: number[];
  /** New uploads. */
  files: File[];
};

const documentLabel = (doc: CandidateDocument) =>
  [doc.number, doc.index, doc.name].filter(Boolean).join(" - ");

const storedLabel = (label: string, index: string | null) =>
  isNil(index) ? label : `${label} (${index})`;

function groupBy(
  docs: CandidateDocument[],
  key: (doc: CandidateDocument) => string,
): [string, CandidateDocument[]][] {
  const groups = new Map<string, CandidateDocument[]>();
  for (const doc of docs) {
    const name = key(doc);
    groups.set(name, [...(groups.get(name) ?? []), doc]);
  }
  return [...groups.entries()];
}

const sectionTitleClass =
  "text-xs font-semibold uppercase tracking-wide text-zinc-500";
const fieldLabelClass = "text-xs font-medium text-zinc-500";
const inputClass =
  "mt-1 w-full rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100";
const linkButtonClass =
  "text-xs text-blue-600 hover:underline disabled:cursor-not-allowed disabled:text-zinc-400 dark:text-blue-400";

export type ObservationConsignesProps = {
  yardId: string;
  observationId: number;
  consignes: Consigne[];
  /** Called after a consigne is saved or deleted. */
  onChanged: () => void;
  onDirtyChange: (observationId: number, isDirty: boolean) => void;
};

/**
 * « Consigne à auteur des documents visés » and « Consigne à autre
 * prestataire » windows of one observation (screens 022 / 023).
 */
export function ObservationConsignes({
  yardId,
  observationId,
  consignes,
  onChanged,
  onDirtyChange,
}: ObservationConsignesProps) {
  const [candidates, setCandidates] = useState<CandidateDocument[] | null>(
    null,
  );
  const [draft, setDraft] = useState<Draft | null>(null);
  const [initialDraft, setInitialDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [diffusionDoc, setDiffusionDoc] = useState<CandidateDocument | null>(
    null,
  );
  const [diffusing, setDiffusing] = useState(false);

  const isDirty =
    isNotNil(draft) &&
    (draft.files.length > 0 ||
      JSON.stringify(draft) !== JSON.stringify(initialDraft));
  useEffect(
    () => onDirtyChange(observationId, isDirty),
    [observationId, isDirty, onDirtyChange],
  );

  const loadCandidates = useCallback(async () => {
    try {
      const { data, error } = await apiClient.GET(
        "/api/yards/{yardId}/observations/{observationId}/consigne-documents",
        { params: { path: { yardId, observationId } } },
      );
      if (isNotNil(error) || isNil(data)) {
        toaster.error({
          title: "Erreur",
          description:
            error?.error ?? "Impossible de charger les documents du chantier",
        });
        return null;
      }
      setCandidates(data.data.items);
      return data.data.items;
    } catch (err) {
      console.error("Failed to load consigne documents", err);
      toaster.error({
        title: "Erreur",
        description: "Impossible de charger les documents du chantier",
      });
      return null;
    }
  }, [yardId, observationId]);

  const openEditor = async (kind: ConsigneKind, consigne?: Consigne) => {
    const items = candidates ?? (await loadCandidates());
    if (isNil(items)) return;
    const next: Draft = isNotNil(consigne)
      ? {
          consigneId: consigne.id,
          kind,
          description: consigne.description,
          documentVersionIds: consigne.documents.map(
            (d) => d.documentVersionId,
          ),
          pieceJointeVersionIds: consigne.piecesJointes
            .map((pj) => pj.documentVersionId)
            .filter(isNotNil),
          fichierIds: consigne.piecesJointes
            .map((pj) => pj.fichierId)
            .filter(isNotNil),
          files: [],
        }
      : {
          consigneId: null,
          kind,
          description: "",
          // The documents visés come first: preselected for their author.
          documentVersionIds:
            kind === "auteur"
              ? items
                  .filter((doc) => doc.scope === "vise")
                  .map((doc) => doc.documentVersionId)
              : [],
          pieceJointeVersionIds: [],
          fichierIds: [],
          files: [],
        };
    setDraft(next);
    setInitialDraft(next);
  };

  const closeEditor = () => {
    setDraft(null);
    setInitialDraft(null);
  };

  const updateDraft = (patch: Partial<Draft>) =>
    setDraft((prev) => (isNil(prev) ? prev : { ...prev, ...patch }));

  const save = async () => {
    if (isNil(draft)) return;
    const metadata = JSON.stringify({
      description: draft.description,
      documentVersionIds: draft.documentVersionIds,
      pieceJointeVersionIds: draft.pieceJointeVersionIds,
      ...(isNil(draft.consigneId)
        ? { kind: draft.kind }
        : { fichierIds: draft.fichierIds }),
    });
    const bodySerializer = () => {
      const fd = new FormData();
      fd.append("metadata", metadata);
      for (const file of draft.files) fd.append("pj", file);
      return fd;
    };
    setSaving(true);
    try {
      const { error } = isNil(draft.consigneId)
        ? await apiClient.POST(
            "/api/yards/{yardId}/observations/{observationId}/consignes",
            {
              params: { path: { yardId, observationId } },
              body: { metadata },
              bodySerializer,
            },
          )
        : await apiClient.PATCH(
            "/api/yards/{yardId}/observations/{observationId}/consignes/{consigneId}",
            {
              params: {
                path: { yardId, observationId, consigneId: draft.consigneId },
              },
              body: { metadata },
              bodySerializer,
            },
          );
      if (isNotNil(error)) {
        toaster.error({
          title: "Erreur",
          description:
            error.error ?? "Échec de l'enregistrement de la consigne",
        });
        return;
      }
      toaster.success({
        title: "Consigne enregistrée",
        description: "La consigne a été enregistrée",
      });
      closeEditor();
      onChanged();
    } catch (err) {
      console.error("Failed to save consigne", err);
      toaster.error({
        title: "Erreur",
        description: "Échec de l'enregistrement de la consigne",
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (consigne: Consigne) => {
    const confirmed = await confirmDialog({
      title: "Supprimer la consigne",
      message: "Supprimer cette consigne ?",
      confirmLabel: "Supprimer",
    });
    if (!confirmed) return;
    try {
      const { error } = await apiClient.DELETE(
        "/api/yards/{yardId}/observations/{observationId}/consignes/{consigneId}",
        {
          params: {
            path: { yardId, observationId, consigneId: consigne.id },
          },
        },
      );
      if (isNotNil(error)) {
        toaster.error({
          title: "Erreur",
          description: error.error ?? "Échec de la suppression",
        });
        return;
      }
      onChanged();
    } catch (err) {
      console.error("Failed to delete consigne", err);
      toaster.error({
        title: "Erreur",
        description: "Échec de la suppression",
      });
    }
  };

  const openFichier = async (fichierId: number) => {
    const { data } = await apiClient.GET(
      "/api/yards/{yardId}/fichiers/{fichierId}/url",
      { params: { path: { yardId, fichierId: String(fichierId) } } },
    );
    if (isNotNil(data)) {
      window.open(data.data.inlineUrl, "_blank", "noopener,noreferrer");
    }
  };

  const markBpe = async (doc: CandidateDocument) => {
    const confirmed = await confirmDialog({
      title: "Marquer BPE",
      message: "Marquer cette version comme BPE ?",
      confirmLabel: "Marquer BPE",
    });
    if (!confirmed) return;
    const { error } = await apiClient.POST(
      "/api/yards/{yardId}/documents/{documentId}/versions/{versionId}/bpe",
      {
        params: {
          path: {
            yardId,
            documentId: String(doc.documentId),
            versionId: String(doc.documentVersionId),
          },
        },
      },
    );
    if (isNotNil(error)) {
      toaster.error({
        title: "Erreur",
        description: error.error ?? "Impossible de marquer BPE",
      });
      return;
    }
    await loadCandidates();
  };

  const confirmDiffusion = async (
    targets: DiffusionTarget[],
    avisRequis: boolean,
  ) => {
    if (isNil(diffusionDoc)) return;
    setDiffusing(true);
    try {
      const { error } = await apiClient.POST(
        "/api/yards/{yardId}/documents/{documentId}/versions/{versionId}/diffusion",
        {
          params: {
            path: {
              yardId,
              documentId: String(diffusionDoc.documentId),
              versionId: String(diffusionDoc.documentVersionId),
            },
          },
          body: { targets, avisRequis },
        },
      );
      if (isNotNil(error)) {
        toaster.error({
          title: "Erreur",
          description:
            error.error ?? "Erreur lors de l'ajout à la liste de diffusion",
        });
        return;
      }
      toaster.success({
        title: "Mis en diffusion",
        description: `${documentLabel(diffusionDoc)} est dans la liste de diffusion`,
      });
      setDiffusionDoc(null);
    } finally {
      setDiffusing(false);
    }
  };

  const candidateByVersion = new Map(
    (candidates ?? []).map((doc) => [doc.documentVersionId, doc]),
  );

  const renderDocumentActions = (doc: CandidateDocument) => {
    const label = documentLabel(doc);
    return (
      <span className="flex shrink-0 items-center gap-2">
        <button
          type="button"
          onClick={() => markBpe(doc)}
          disabled={doc.bpe || !doc.canMarkBpe}
          aria-label={doc.bpe ? `${label} est BPE` : `Marquer BPE ${label}`}
          title={doc.bpe ? "BPE" : doc.canMarkBpe ? "Marquer BPE" : ""}
          className={cn(
            doc.bpe
              ? "cursor-default text-green-600 dark:text-green-400"
              : doc.canMarkBpe
                ? "text-zinc-400 hover:text-green-500"
                : "cursor-default text-zinc-200 dark:text-zinc-700",
          )}
        >
          <SealIcon size={16} weight={doc.bpe ? "fill" : "regular"} />
        </button>
        {doc.piloted && (
          <button
            type="button"
            onClick={() => setDiffusionDoc(doc)}
            aria-label={`Diffuser ${label}`}
            title="Diffuser"
            className="text-zinc-400 hover:text-blue-600 dark:hover:text-blue-400"
          >
            <PaperPlaneIcon size={14} />
          </button>
        )}
        <span className="w-28">
          {doc.piloted ? (
            <NoticeFlagSelect
              yardId={yardId}
              documentId={doc.documentId}
              documentLabel={`${doc.number} - ${doc.name}`}
              value={doc.noticeFlag}
              onChanged={() => void loadCandidates()}
            />
          ) : (
            <span
              className={cn(
                "inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs",
                NOTICE_FLAG_CLASSES[doc.noticeFlag],
              )}
            >
              {NOTICE_FLAG_LABELS[doc.noticeFlag]}
            </span>
          )}
        </span>
      </span>
    );
  };

  const renderEditor = (current: Draft, consigne?: Consigne) => {
    const inputId = `consigne-${observationId}-${current.consigneId ?? `new-${current.kind}`}`;
    const suggestionsId = `${inputId}-suggestions`;
    const storedDocuments = new Map(
      (consigne?.documents ?? []).map((d) => [
        d.documentVersionId,
        storedLabel(d.label, d.index),
      ]),
    );
    const storedPiecesJointes = consigne?.piecesJointes ?? [];
    const offered = (candidates ?? []).filter((doc) =>
      current.kind === "auteur"
        ? doc.scope !== "autre_auteur"
        : doc.scope === "autre_auteur",
    );
    const documentGroups = groupBy(offered, (doc) =>
      current.kind === "auteur"
        ? SCOPE_GROUPS[doc.scope]
        : doc.authorCompanyName || "Sans auteur",
    );
    const attachableGroups = groupBy(
      candidates ?? [],
      (doc) => doc.authorCompanyName || "Sans auteur",
    );
    const canSave =
      !saving &&
      current.description.trim().length > 0 &&
      current.documentVersionIds.length > 0;

    return (
      <form
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
        className="space-y-3 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/30"
      >
        <div>
          <label htmlFor={inputId} className={fieldLabelClass}>
            Consigne
          </label>
          <input
            id={inputId}
            type="text"
            required
            maxLength={10000}
            list={current.kind === "auteur" ? suggestionsId : undefined}
            value={current.description}
            onChange={(event) =>
              updateDraft({ description: event.target.value })
            }
            placeholder={
              current.kind === "auteur"
                ? "À prendre en compte en l'état, proposer réponse, ou consigne saisie à la main"
                : "Consigne saisie à la main"
            }
            className={inputClass}
          />
          {current.kind === "auteur" && (
            <datalist id={suggestionsId}>
              {AUTEUR_SUGGESTIONS.map((suggestion) => (
                <option key={suggestion} value={suggestion} />
              ))}
            </datalist>
          )}
        </div>

        <div>
          <p className={fieldLabelClass}>Documents concernés par la consigne</p>
          {isNil(candidates) ? (
            <p className="mt-1 text-xs text-zinc-500">Chargement...</p>
          ) : (
            <>
              <ul className="mt-1 space-y-1">
                {current.documentVersionIds.map((versionId) => {
                  const doc = candidateByVersion.get(versionId);
                  const label = isNotNil(doc)
                    ? documentLabel(doc)
                    : (storedDocuments.get(versionId) ?? "Document");
                  return (
                    <li
                      key={versionId}
                      className="flex items-center gap-3 rounded bg-white px-2 py-1 text-xs text-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
                    >
                      <span className="min-w-0 flex-1 truncate" title={label}>
                        {label}
                        {isNotNil(doc) && (
                          <span className="text-zinc-400">
                            {` · ${doc.authorCompanyName}`}
                          </span>
                        )}
                      </span>
                      {isNotNil(doc) && renderDocumentActions(doc)}
                      <button
                        type="button"
                        onClick={() =>
                          updateDraft({
                            documentVersionIds:
                              current.documentVersionIds.filter(
                                (id) => id !== versionId,
                              ),
                          })
                        }
                        aria-label={`Retirer ${label}`}
                        className={linkButtonClass}
                      >
                        Retirer
                      </button>
                    </li>
                  );
                })}
              </ul>
              {current.documentVersionIds.length === 0 && (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                  Choisissez au moins un document.
                </p>
              )}
              <select
                aria-label="Ajouter un document"
                value=""
                onChange={(event) => {
                  const versionId = Number(event.target.value);
                  if (versionId > 0) {
                    updateDraft({
                      documentVersionIds: [
                        ...current.documentVersionIds,
                        versionId,
                      ],
                    });
                  }
                }}
                className={inputClass}
              >
                <option value="">
                  {offered.length === 0
                    ? "Aucun document disponible"
                    : "Ajouter un document…"}
                </option>
                {documentGroups.map(([group, docs]) => (
                  <optgroup key={group} label={group}>
                    {docs.map((doc) => (
                      <option
                        key={doc.documentVersionId}
                        value={doc.documentVersionId}
                        disabled={current.documentVersionIds.includes(
                          doc.documentVersionId,
                        )}
                      >
                        {documentLabel(doc)}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </select>
            </>
          )}
        </div>

        <div>
          <p className={fieldLabelClass}>Pièces jointes</p>
          <ul className="mt-1 space-y-1">
            {current.fichierIds.map((fichierId) => (
              <li key={`f-${fichierId}`} className="flex gap-3 text-xs">
                <span className="truncate">
                  {storedPiecesJointes.find((pj) => pj.fichierId === fichierId)
                    ?.label ?? "Fichier"}
                </span>
                <button
                  type="button"
                  className={linkButtonClass}
                  onClick={() =>
                    updateDraft({
                      fichierIds: current.fichierIds.filter(
                        (id) => id !== fichierId,
                      ),
                    })
                  }
                >
                  Retirer
                </button>
              </li>
            ))}
            {current.pieceJointeVersionIds.map((versionId) => {
              const doc = candidateByVersion.get(versionId);
              const label = isNotNil(doc)
                ? documentLabel(doc)
                : (storedPiecesJointes.find(
                    (pj) => pj.documentVersionId === versionId,
                  )?.label ?? "Document");
              return (
                <li key={`v-${versionId}`} className="flex gap-3 text-xs">
                  <span className="truncate">{label}</span>
                  <button
                    type="button"
                    className={linkButtonClass}
                    onClick={() =>
                      updateDraft({
                        pieceJointeVersionIds:
                          current.pieceJointeVersionIds.filter(
                            (id) => id !== versionId,
                          ),
                      })
                    }
                  >
                    Retirer
                  </button>
                </li>
              );
            })}
            {current.files.map((file, index) => (
              <li
                key={`n-${file.name}-${file.size}`}
                className="flex gap-3 text-xs"
              >
                <span className="truncate">{file.name}</span>
                <button
                  type="button"
                  className={linkButtonClass}
                  onClick={() =>
                    updateDraft({
                      files: current.files.filter((_, i) => i !== index),
                    })
                  }
                >
                  Retirer
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-1 flex gap-2">
            <select
              aria-label="Joindre un document ÉtudesCarré"
              value=""
              disabled={isNil(candidates)}
              onChange={(event) => {
                const versionId = Number(event.target.value);
                if (versionId > 0) {
                  updateDraft({
                    pieceJointeVersionIds: [
                      ...current.pieceJointeVersionIds,
                      versionId,
                    ],
                  });
                }
              }}
              className={cn(inputClass, "mt-0 flex-1")}
            >
              <option value="">Joindre un document ÉtudesCarré…</option>
              {attachableGroups.map(([group, docs]) => (
                <optgroup key={group} label={group}>
                  {docs.map((doc) => (
                    <option
                      key={doc.documentVersionId}
                      value={doc.documentVersionId}
                      disabled={current.pieceJointeVersionIds.includes(
                        doc.documentVersionId,
                      )}
                    >
                      {documentLabel(doc)}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
            <input
              type="file"
              multiple
              aria-label="Joindre un fichier"
              onChange={(event) => {
                const added = Array.from(event.target.files ?? []);
                event.target.value = "";
                updateDraft({ files: [...current.files, ...added] });
              }}
              className="flex-1 text-xs"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" outline onClick={closeEditor} disabled={saving}>
            Annuler
          </Button>
          <Button type="submit" disabled={!canSave}>
            {saving ? "Enregistrement..." : "Enregistrer la consigne"}
          </Button>
        </div>
      </form>
    );
  };

  return (
    <>
      {KINDS.map(({ kind, title }) => {
        const ofKind = consignes.filter((consigne) => consigne.kind === kind);
        const addingHere =
          isNotNil(draft) && isNil(draft.consigneId) && draft.kind === kind;
        return (
          <section key={kind} className="mt-4">
            <div className="flex items-center justify-between gap-2">
              <h4 className={sectionTitleClass}>{title}</h4>
              <Button
                type="button"
                outline
                onClick={() => void openEditor(kind)}
                disabled={isNotNil(draft)}
              >
                Ajouter une consigne
              </Button>
            </div>
            {ofKind.length === 0 && !addingHere && (
              <p className="mt-1 text-xs text-zinc-400">Aucune consigne</p>
            )}
            <ul className="mt-2 space-y-2">
              {ofKind.map((consigne) =>
                draft?.consigneId === consigne.id ? (
                  <li key={consigne.id}>{renderEditor(draft, consigne)}</li>
                ) : (
                  <li
                    key={consigne.id}
                    className="rounded-md border border-zinc-200 p-2 text-sm dark:border-zinc-700"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <p className="text-zinc-900 dark:text-zinc-100">
                        {consigne.description}
                      </p>
                      <span className="flex shrink-0 gap-3">
                        <button
                          type="button"
                          className={linkButtonClass}
                          disabled={isNotNil(draft)}
                          onClick={() => void openEditor(kind, consigne)}
                        >
                          Modifier
                        </button>
                        <button
                          type="button"
                          className={linkButtonClass}
                          disabled={isNotNil(draft)}
                          onClick={() => void remove(consigne)}
                        >
                          Supprimer
                        </button>
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-zinc-500">
                      {`Documents : ${consigne.documents
                        .map((d) => storedLabel(d.label, d.index))
                        .join(", ")}`}
                    </p>
                    {consigne.piecesJointes.length > 0 && (
                      <p className="mt-1 flex flex-wrap gap-x-3 text-xs text-zinc-500">
                        <span>Pièces jointes :</span>
                        {consigne.piecesJointes.map((pj) =>
                          isNotNil(pj.fichierId) ? (
                            <button
                              key={pj.id}
                              type="button"
                              className={linkButtonClass}
                              onClick={() => {
                                if (isNotNil(pj.fichierId)) {
                                  void openFichier(pj.fichierId);
                                }
                              }}
                            >
                              {pj.label}
                            </button>
                          ) : (
                            <span key={pj.id}>{pj.label}</span>
                          ),
                        )}
                      </p>
                    )}
                  </li>
                ),
              )}
              {addingHere && <li>{renderEditor(draft)}</li>}
            </ul>
          </section>
        );
      })}
      <DiffusionDialog
        open={isNotNil(diffusionDoc)}
        onClose={() => setDiffusionDoc(null)}
        documentName={
          isNil(diffusionDoc)
            ? ""
            : `${diffusionDoc.number} - ${diffusionDoc.name}`
        }
        versionIndex={diffusionDoc?.index ?? null}
        onConfirm={confirmDiffusion}
        loading={diffusing}
      />
    </>
  );
}
